import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { DocumentsFileRequest, DocumentsFolderRequest, DocumentsRootFolderRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";
import type { ApiResponseType } from "@/types/api-types";

const useDeleteRootFolder = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (id: string) => api.deleteDocumentsRootFolderByIdV1(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DOCUMENTS_ROOT_LIST(projectId) });
      showFlashToast({
        variant: "success",
        message: t("model.document.root.action.delete.notification.success"),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("model.document.root.action.delete.notification.failure"),
      });
    },
  });
};

const useDeleteFolder = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: { parentFolderId: string; folderId: string }) =>
      api.deleteDocumentsFolderByIdV1(payload.folderId),
    onSuccess: (_, payload) => {
      showFlashToast({
        variant: "success",
        message: t("model.document.folder.action.delete.notification.success"),
      });

      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DOCUMENTS_LIST(projectId, payload.parentFolderId) });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("model.document.folder.action.delete.notification.failure"),
      });
    },
  });
};

const useDeleteFile = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: { folderId: string; fileId: string }) => api.deleteDocumentsFileByIdV1(payload.fileId),
    onSuccess: (_, payload) => {
      showFlashToast({
        variant: "success",
        message: t("model.document.file.action.delete.notification.success"),
      });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DOCUMENTS_LIST(projectId, payload.folderId) });
    },

    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("model.document.file.action.delete.notification.failure"),
      });
    },
  });
};

const useUpdateFileViewCount = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: { folderId: string; fileId: string; page?: number }) =>
      api.putDocumentsUpdateViewCountV1(payload.fileId),
    onMutate: (payload) => {
      const pageSize = 10;

      queryClient.setQueryData<ApiResponseType<"getDocumentsDetailsV1">>(
        QUERY_KEYS.DOCUMENTS_LIST(projectId, payload.folderId, {
          Offset: pageSize * (payload.page || 0),
          Limit: pageSize,
        }),
        (currData) => {
          if (!currData) return currData;

          const currFiles = currData.data.items;
          const updatedFiles = currFiles.map((file) => {
            if (file.id === payload.fileId) {
              return { ...file, viewCount: (file.viewCount || 0) + 1 };
            }

            return file;
          });

          return {
            ...currData,
            data: {
              ...currData.data,
              items: updatedFiles,
            },
          };
        },
      );
    },
    onSettled: (_, __, payload) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DOCUMENTS_LIST(projectId, payload.folderId) });
    },
  });
};

const useCreateRootFolder = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (data: DocumentsRootFolderRequest) => api.postDocumentsRootFolderV1(data),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("model.document.root.action.create.notification.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("model.document.root.action.create.notification.failure") });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DOCUMENTS_ROOT_LIST(projectId) });
    },
  });
};

const useUpdateRootFolder = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DocumentsRootFolderRequest }) =>
      api.putDocumentsRootFolderV1(id, body),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("model.document.root.action.edit.notification.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("model.document.root.action.edit.notification.failure") });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DOCUMENTS_ROOT_LIST(projectId) });
    },
  });
};

const useCreateFolder = (folderId: string) => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (data: DocumentsFolderRequest) => api.postDocumentsFolderV1(data),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("model.document.folder.action.create.notification.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("model.document.folder.action.create.notification.failure") });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DOCUMENTS_LIST(projectId, folderId) });
    },
  });
};

const useUpdateFolder = (folderId: string) => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (body: DocumentsFolderRequest) => api.putDocumentsFolderV1(folderId, body),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("model.document.folder.action.edit.notification.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("model.document.folder.action.edit.notification.failure") });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DOCUMENTS_LIST(projectId, folderId) });
    },
  });
};

const useCreateFile = (folderId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (document: DocumentsFileRequest) => api.postDocumentsFileV1(document),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DOCUMENTS_LIST(projectId, folderId) });
    },
  });
};

const useUpdateFile = (fileId: string, folderId: string) => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (document: DocumentsFileRequest) => api.putDocumentsFileV1(fileId, document),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("model.document.file.action.edit.notification.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("model.document.file.action.edit.notification.error") });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DOCUMENTS_LIST(projectId, folderId) });
    },
  });
};

export const documentMutations = {
  useDeleteFile,
  useUpdateFileViewCount,
  useDeleteRootFolder,
  useDeleteFolder,
  useCreateRootFolder,
  useUpdateRootFolder,
  useCreateFolder,
  useUpdateFolder,
  useCreateFile,
  useUpdateFile,
};
